import React from 'react';
import { useField } from 'react-final-form';

import { autocompletion, closeBrackets, closeBracketsKeymap, completionKeymap } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { html } from '@codemirror/lang-html';
import { liquid } from '@codemirror/lang-liquid';
import {
  bracketMatching,
  defaultHighlightStyle,
  foldGutter,
  foldKeymap,
  indentOnInput,
  syntaxHighlighting,
} from '@codemirror/language';
import { lintGutter, lintKeymap } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';
import {
  drawSelection,
  dropCursor,
  EditorView,
  highlightActiveLineGutter,
  highlightSpecialChars,
  keymap,
  lineNumbers,
} from '@codemirror/view';
import { Box } from '@mui/material';

const LIQUID_HTML_EDITOR = [
  lintGutter(),
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  foldGutter(),
  drawSelection(),
  dropCursor(),
  indentOnInput(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  bracketMatching(),
  closeBrackets(),
  autocompletion(),
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...completionKeymap,
    ...lintKeymap,
    indentWithTab,
  ]),
  liquid({ base: html() }),
];

type BaseCodeMirrorInputProps = {
  label: string;
  resizeVertical?: boolean;
  helperText?: JSX.Element;
};

type CoreCodeMirrorInputProps = BaseCodeMirrorInputProps & {
  value: string;
  onChange: (value: string) => void;
};

class CoreCodeMirrorInput extends React.Component<CoreCodeMirrorInputProps> {
  private containerRef: React.RefObject<HTMLDivElement>;

  constructor(props: CoreCodeMirrorInputProps) {
    super(props);
    this.containerRef = React.createRef();
  }

  componentDidMount(): void {
    const containerRef = this.containerRef;
    if (containerRef.current === null) {
      return;
    }

    new EditorView({
      parent: containerRef.current,
      state: EditorState.create({
        doc: this.props.value ?? '',
        selection: { anchor: 0 },
        extensions: [
          LIQUID_HTML_EDITOR,
          EditorView.updateListener.of((vu) => {
            if (vu.docChanged) {
              this.props.onChange(vu.state.doc.toString());
            }
          }),
        ],
      }),
    });
  }

  render() {
    const { label, resizeVertical, helperText } = this.props;
    return (
      <>
        {label}
        <Box
          ref={this.containerRef}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            '& .cm-editor': {
              minHeight: 232,
              resize: resizeVertical ? 'vertical' : undefined,
              overflow: 'hidden',
            },
            '& .cm-content, & .cm-gutter': {
              minHeight: 232,
            },
            '& .cm-focused': {
              outlineStyle: 'solid',
              outlineColor: '#9e9e9e',
            },
          }}
        />
        {helperText}
      </>
    );
  }
}

type CodeMirrorInputProps = BaseCodeMirrorInputProps & {
  name: string;
};
export default function CodeMirrorInput({ name, ...props }: CodeMirrorInputProps) {
  const { value, onChange } = useField(name).input;

  return <CoreCodeMirrorInput value={value} onChange={onChange} {...props} />;
}
